import type {
	DeleteScenarioRunParams,
	DeleteScenarioRunSetParams,
	HTTPValidationError,
	ScenarioCreateDataModel,
	ScenarioMainCopySettings,
	ScenarioModel,
	ScenarioRunSetCreateModel,
	ScenarioRunSetModel,
	ScenarioRunSetRestartModel,
	ScenarioSetMainDataModel,
	ScenarioUpdateDataModel,
} from '@/shared/generated'
import type { UseMutationOptions } from '@tanstack/react-query'
import type { AxiosError } from 'axios'

import { useMutation } from '@tanstack/react-query'

import { client } from '@/shared/api/client'

/**
 * Hook for create a new scenario in optimization group
 * @param options - Mutate options
 * @returns
 */
export function useCreateScenarioMutation(
	options?: UseMutationOptions<
		ScenarioModel,
		AxiosError<HTTPValidationError>,
		ScenarioCreateDataModel
	>
) {
	return useMutation<
		ScenarioModel,
		AxiosError<HTTPValidationError>,
		ScenarioCreateDataModel
	>({
		mutationFn: (payload) => client.scenario.scenarioCreate(payload),
		...(options || []),
	})
}

/**
 * Hook for update a scenario in optimization group
 * @param scenarioId - Scenario id
 * @param options - Mutate options
 * @returns - Mutate function
 */
export function useUpdateScenarioMutation(
	scenarioId: string,
	options?: UseMutationOptions<
		ScenarioModel,
		AxiosError<HTTPValidationError>,
		ScenarioUpdateDataModel
	>
) {
	return useMutation<
		ScenarioModel,
		AxiosError<HTTPValidationError>,
		ScenarioUpdateDataModel
	>({
		mutationFn: (payload) =>
			client.scenario.scenarioUpdate({ scenarioId }, payload),
		...(options || []),
	})
}

/**
 * Hook for delete a scenario in optimization group
 * @param options - Mutate options
 * @returns - Mutate function
 */
export function useDeleteScenarioMutation(
	options?: UseMutationOptions<void, AxiosError<HTTPValidationError>, string[]>
) {
	return useMutation<void, AxiosError<HTTPValidationError>, string[]>({
		mutationKey: ['scenario-delete'],
		mutationFn: (payload) => client.scenario.deleteScenariosBulk(payload),
		...(options || []),
	})
}

/**
 * Hook for duplicate a scenario in optimization group
 * @param options - Mutate options
 * @returns - Mutate function
 */
export function useDuplicateScenarioMutation(
	options?: UseMutationOptions<
		ScenarioModel,
		AxiosError<HTTPValidationError>,
		string
	>
) {
	return useMutation<ScenarioModel, AxiosError<HTTPValidationError>, string>({
		mutationKey: ['scenario-duplicate'],
		mutationFn: (scenarioId) =>
			client.scenario.scenarioDuplicate({ scenarioId }),
		...(options || []),
	})
}

export function useRestartScenariosMutation(
	options?: UseMutationOptions<
		ScenarioRunSetModel,
		AxiosError<HTTPValidationError>,
		ScenarioRunSetRestartModel
	>
) {
	return useMutation<
		ScenarioRunSetModel,
		AxiosError<HTTPValidationError>,
		ScenarioRunSetRestartModel
	>({
		mutationFn: (payload) =>
			client.scenarioRunSets.restartScenarioRunSet(payload),
		...(options || []),
	})
}
export function useDeleteScenarioRunMutation(
	params: DeleteScenarioRunParams,
	options?: UseMutationOptions<void, AxiosError<HTTPValidationError>>
) {
	return useMutation<void, AxiosError<HTTPValidationError>>({
		mutationFn: () => client.scenarioRunSets.deleteScenarioRun(params),
		...(options || []),
	})
}
export function useRunScenarioSetsMutation(
	options?: UseMutationOptions<
		ScenarioRunSetModel,
		AxiosError<HTTPValidationError>,
		ScenarioRunSetCreateModel
	>
) {
	return useMutation<
		ScenarioRunSetModel,
		AxiosError<HTTPValidationError>,
		ScenarioRunSetCreateModel
	>({
		mutationKey: ['run-scenario-sets'],
		mutationFn: (payload) =>
			client.scenarioRunSets.createScenarioRunSet(payload),
		...(options || []),
	})
}
export function useSetMainScenarioMutation(
	options?: UseMutationOptions<
		ScenarioModel,
		AxiosError<HTTPValidationError>,
		{
			scenario_id: string
			payload: ScenarioSetMainDataModel
		}
	>
) {
	return useMutation<
		ScenarioModel,
		AxiosError<HTTPValidationError>,
		{
			scenario_id: string
			payload: ScenarioSetMainDataModel
		}
	>({
		mutationFn: ({ scenario_id, payload }) =>
			client.scenario.scenarioSetMain({ scenarioId: scenario_id }, payload),
		...(options || []),
	})
}

/**
 * Hook for duplicate a scenario in optimization group
 * @param options - Mutate options
 * @payload - string[]
 * @returns - Mutate function
 */
export function useDeleteScenarioRunSetMutation(
	options?: UseMutationOptions<
		void,
		AxiosError<HTTPValidationError>,
		DeleteScenarioRunSetParams
	>
) {
	return useMutation<
		void,
		AxiosError<HTTPValidationError>,
		DeleteScenarioRunSetParams
	>({
		mutationFn: (payload) =>
			client.scenarioRunSets.deleteScenarioRunSet(payload),
		...(options || []),
	})
}

export function useUpdateMainScenarioMutation(
	optimizationGroupId: string,
	options?: UseMutationOptions<
		ScenarioModel,
		AxiosError,
		ScenarioUpdateDataModel
	>
) {
	return useMutation<ScenarioModel, AxiosError, ScenarioUpdateDataModel>({
		mutationFn: (payload) =>
			client.scenario.scenarioUpdateMain({ optimizationGroupId }, payload),
		...(options || {}),
	})
}
export function useCopyMainScenarioSettingsMutation(
	optimizationGroupId: string,
	options?: UseMutationOptions<
		{
			success?: boolean
			optimization_group_id?: string
		}[],
		AxiosError,
		ScenarioMainCopySettings & {
			dst_optimization_group_ids: string[]
		}
	>
) {
	return useMutation<
		{
			success?: boolean
			optimization_group_id?: string
		}[],
		AxiosError,
		ScenarioMainCopySettings & {
			dst_optimization_group_ids: string[]
		}
	>({
		mutationFn: (payload) =>
			client.scenario.scenarioMainCopySettings(
				{ optimizationGroupId },
				payload
			),
		...(options || {}),
	})
}
